export interface SimEventData {
  step: string;
  detail: string;
  tag?: string;
  payload?: {
    amount: number;
    quantity: number;
    location_name: string;
    payment_type: string;
  };
  respTime?: number;
}

export interface SimScenario {
  id: string;
  icon: string;
  iconClass: string;
  name: string;
  sub: string;
  accentColor: string;
  tab: 'ev' | 'food' | 'mall' | 'gas' | 'charges' | 'partner';
  activityType: string;
  eventsData: SimEventData[];
}

export const EV_SCENARIOS: SimScenario[] = [
  {
    id: 'ev1',
    icon: '⚡',
    iconClass: 'ev-bg',
    name: 'Fast Charge — Morning Commute',
    sub: 'CCD Charging Hub · Slot 04A',
    accentColor: 'ev',
    tab: 'ev',
    activityType: 'ev_charging',
    eventsData: [
      {
        step: 'User parks vehicle at CCD Charging Hub',
        detail: 'RFID card tapped · Slot 04A assigned · <span>7:42 AM</span>',
      },
      {
        step: 'Charging session initiated',
        detail:
          'Connector type: <span>CCS2</span> · Target: <span>80%</span> · Max rate: <span>50 kW</span>',
      },
      {
        step: 'Real-time usage metered',
        detail:
          'Energy drawn: <span>14.2 kWh</span> · Duration: <span>22 min</span> · Cost/unit: <span>₹12.50</span>',
      },
      {
        step: 'Payment captured via UPI',
        detail:
          'Amount: <span>₹177</span> · UPI Ref: <span>TXN2025061204A</span> · Status: <span>SUCCESS</span>',
        payload: {
          amount: 177,
          quantity: 14.2,
          location_name: 'CCD Charging Hub – Slot 04A',
          payment_type: 'UPI',
        },
        respTime: 142,
      },
    ],
  },
  {
    id: 'ev2',
    icon: '⚡',
    iconClass: 'ev-bg',
    name: 'Overnight Slow Charge',
    sub: 'Basement B2 · Slot 12',
    accentColor: 'ev',
    tab: 'ev',
    activityType: 'ev_charging',
    eventsData: [
      {
        step: 'Vehicle plugged in at Basement B2',
        detail: 'User: <span>Rajesh Kumar</span> · Slot 12 · <span>11:55 PM</span>',
      },
      {
        step: 'Smart charging schedule activated',
        detail:
          'Off-peak rate mode · Target: <span>100%</span> · Est. duration: <span>5h 20m</span>',
      },
      {
        step: 'Session complete at 5:18 AM',
        detail: 'Energy: <span>36.8 kWh</span> · Cost/unit: <span>₹8.00</span> (off-peak)',
      },
      {
        step: 'Auto-payment via linked card',
        detail:
          'Amount: <span>₹294</span> · Card: <span>****4891</span> · Auth: <span>APRVD</span>',
        payload: {
          amount: 294,
          quantity: 36.8,
          location_name: 'Basement B2 – Slot 12',
          payment_type: 'Card',
        },
        respTime: 198,
      },
    ],
  },
];

export const FOOD_SCENARIOS: SimScenario[] = [
  {
    id: 'food1',
    icon: '🍽',
    iconClass: 'food-bg',
    name: 'Lunch Order — Wok to Walk',
    sub: 'Food Court Level 1 · Table 7',
    accentColor: 'food',
    tab: 'food',
    activityType: 'food_court',
    eventsData: [
      {
        step: 'QR code scanned at Table 7',
        detail: 'Outlet: <span>Wok to Walk</span> · Floor 1 · <span>1:08 PM</span>',
      },
      {
        step: 'Order placed via digital menu',
        detail: 'Chicken Noodles × 1, Cold Coffee × 2 · Subtotal: <span>₹580</span>',
      },
      {
        step: 'Kitchen confirmation received',
        detail:
          'Order ID: <span>ORD-20250612-0471</span> · Est. ready: <span>12 min</span>',
      },
      {
        step: 'Payment via UPI',
        detail: 'Amount: <span>₹580</span> (incl. GST) · UPI: <span>TXN2025061209B</span>',
        payload: {
          amount: 580,
          quantity: 3,
          location_name: 'Wok to Walk – Food Court L1',
          payment_type: 'UPI',
        },
        respTime: 112,
      },
    ],
  },
  {
    id: 'food2',
    icon: '☕',
    iconClass: 'food-bg',
    name: 'Evening Snack — Café Indulge',
    sub: 'Food Court Level 2 · Counter 3',
    accentColor: 'food',
    tab: 'food',
    activityType: 'food_court',
    eventsData: [
      {
        step: 'Walk-up order at counter',
        detail: 'Outlet: <span>Café Indulge</span> · Counter 3 · <span>5:45 PM</span>',
      },
      {
        step: 'Items selected',
        detail: 'Mango Shake × 1, Samosa Chaat × 2 · Pre-GST: <span>₹218</span>',
      },
      {
        step: 'Billing generated',
        detail: 'Bill No: <span>CF-0831</span> · GST 5%: <span>₹11</span> · Total: <span>₹229</span>',
      },
      {
        step: 'Wallet payment processed',
        detail: 'Wallet: <span>PhonePe</span> · Amount: <span>₹229</span> · Ref: <span>PH9302A</span>',
        payload: {
          amount: 229,
          quantity: 3,
          location_name: 'Café Indulge – Food Court L2',
          payment_type: 'Wallet',
        },
        respTime: 95,
      },
    ],
  },
];

export const MALL_SCENARIOS: SimScenario[] = [
  {
    id: 'mall1',
    icon: '👟',
    iconClass: 'mall-bg',
    name: 'Footwear Purchase — Nike Store',
    sub: 'Level 3 · Zone A',
    accentColor: 'mall',
    tab: 'mall',
    activityType: 'multistore',
    eventsData: [
      {
        step: 'Customer entered Nike flagship store',
        detail: 'Entry scanned via loyalty app · Level 3 Zone A · <span>3:22 PM</span>',
      },
      {
        step: 'Trial completed, item selected',
        detail: 'Air Max 270 React · UK Size 9 · MRP: <span>₹8,995</span>',
      },
      {
        step: 'Discount applied — member coupon',
        detail:
          'Coupon: <span>NIKECLUB10</span> · Discount: <span>₹899</span> · Payable: <span>₹8,096</span>',
      },
      {
        step: 'Card payment at POS terminal',
        detail:
          'Amount: <span>₹8,096</span> · Card: <span>****2241</span> · Auth: <span>HDFC123</span>',
        payload: {
          amount: 8096,
          quantity: 1,
          location_name: 'Nike – Level 3 Zone A',
          payment_type: 'Card',
        },
        respTime: 167,
      },
    ],
  },
  {
    id: 'mall2',
    icon: '🛒',
    iconClass: 'mall-bg',
    name: 'Grocery Run — Reliance SMART',
    sub: 'Ground Floor · Zone B',
    accentColor: 'mall',
    tab: 'mall',
    activityType: 'multistore',
    eventsData: [
      {
        step: 'Cart opened in Reliance SMART',
        detail: 'Ground Floor Zone B · Member card scanned · <span>11:15 AM</span>',
      },
      {
        step: 'Multi-category items added',
        detail: 'Dairy × 4, Beverages × 6, Snacks × 3 · 13 items total',
      },
      {
        step: 'Smart checkout — no queue',
        detail: 'Self-checkout lane · Scan complete · GST computed',
      },
      {
        step: 'Net Banking payment',
        detail:
          'Amount: <span>₹1,240</span> · IFSC: <span>KKBK0000</span> · Ref: <span>KKB2025A</span>',
        payload: {
          amount: 1240,
          quantity: 13,
          location_name: 'Reliance SMART – GF Zone B',
          payment_type: 'Net Banking',
        },
        respTime: 203,
      },
    ],
  },
];

export const GAS_SCENARIOS: SimScenario[] = [
  {
    id: 'gas1',
    icon: '⛽',
    iconClass: 'gas-bg',
    name: 'Full Tank — Indian Oil Pump',
    sub: 'Entry Gate Forecourt',
    accentColor: 'gas',
    tab: 'gas',
    activityType: 'gas_refill',
    eventsData: [
      {
        step: 'Vehicle enters forecourt',
        detail:
          'Nozzle: <span>Dispenser 3</span> · Fuel type: <span>Petrol</span> · <span>8:10 AM</span>',
      },
      {
        step: 'Loyalty card identified',
        detail: 'Customer: <span>Rajesh Kumar</span> · Existing points: <span>840</span>',
      },
      {
        step: 'Refill metered',
        detail:
          'Volume: <span>32.5L</span> · Rate: <span>₹106.31/L</span> · Total: <span>₹3,455</span>',
      },
      {
        step: 'UPI payment + points earned',
        detail:
          'Amount: <span>₹3,455</span> · Points earned: <span>+34</span> · UPI: <span>TXN20256103</span>',
        payload: {
          amount: 3455,
          quantity: 32.5,
          location_name: 'Indian Oil – Entry Forecourt',
          payment_type: 'UPI',
        },
        respTime: 130,
      },
    ],
  },
  {
    id: 'gas2',
    icon: '⛽',
    iconClass: 'gas-bg',
    name: 'Quick Top-up — HP Pump',
    sub: 'Exit Gate · Lane 2',
    accentColor: 'gas',
    tab: 'gas',
    activityType: 'gas_refill',
    eventsData: [
      {
        step: 'Card tap at HP AutoPay dispenser',
        detail: 'Lane 2 Exit · Fuel: <span>Speed 97</span> · <span>6:50 PM</span>',
      },
      {
        step: 'Pre-authorisation completed',
        detail: 'Card: <span>****9912</span> · Pre-auth: <span>₹2,000 hold</span>',
      },
      {
        step: 'Dispensing stopped at target',
        detail:
          'Volume: <span>15.0L</span> · Rate: <span>₹112.48/L</span> · Total: <span>₹1,687</span>',
      },
      {
        step: 'Final charge captured',
        detail: 'Charged: <span>₹1,687</span> · Hold released · Receipt emailed',
        payload: {
          amount: 1687,
          quantity: 15.0,
          location_name: 'HP Speed – Exit Gate Lane 2',
          payment_type: 'Card',
        },
        respTime: 118,
      },
    ],
  },
];

export const CHARGES_SCENARIOS: SimScenario[] = [
  {
    id: 'charges1',
    icon: '🏬',
    iconClass: 'gas2-bg',
    name: 'Valet Parking — Premium Bay',
    sub: 'P2 Basement · Bay 007',
    accentColor: 'charges',
    tab: 'charges',
    activityType: 'mall_charges',
    eventsData: [
      {
        step: 'Vehicle handed to valet at main gate',
        detail: 'Token: <span>#VT-0094</span> · Time in: <span>10:05 AM</span>',
      },
      {
        step: 'Premium bay assigned',
        detail: 'Bay 007 · P2 Basement · Est. stay: <span>3–4 hrs</span>',
      },
      {
        step: 'Checkout requested via app',
        detail:
          'Duration: <span>3h 22m</span> · Rate: <span>₹80/hr</span> · Subtotal: <span>₹267</span>',
      },
      {
        step: 'Payment at exit kiosk',
        detail: 'Amount: <span>₹267</span> · Cash · Change returned: <span>₹33</span>',
        payload: {
          amount: 267,
          quantity: 1,
          location_name: 'Valet – P2 Bay 007',
          payment_type: 'Cash',
        },
        respTime: 88,
      },
    ],
  },
  {
    id: 'charges2',
    icon: '🎪',
    iconClass: 'gas2-bg',
    name: 'Event Entry Fee — Weekend Expo',
    sub: 'Exhibition Hall · Gate B',
    accentColor: 'charges',
    tab: 'charges',
    activityType: 'mall_charges',
    eventsData: [
      {
        step: 'Event ticket purchased at counter',
        detail:
          'Event: <span>Home & Design Expo</span> · Gate B · <span>Sat, 12 Jun</span>',
      },
      {
        step: 'Ticket category selected',
        detail: 'Adult General: <span>2 tickets × ₹150</span> · Total: <span>₹300</span>',
      },
      {
        step: 'Entry wristbands issued',
        detail: 'Wristband IDs: <span>#WB-4412 / #WB-4413</span> · Valid till 9PM',
      },
      {
        step: 'UPI payment processed',
        detail: 'Amount: <span>₹300</span> · Ref: <span>EXPO2025062201</span>',
        payload: {
          amount: 300,
          quantity: 2,
          location_name: 'Exhibition Hall – Gate B',
          payment_type: 'UPI',
        },
        respTime: 107,
      },
    ],
  },
];

export const PARTNER_SCENARIOS: SimScenario[] = [
  {
    id: 'partner1',
    icon: '🛵',
    iconClass: 'partner-bg',
    name: 'Swiggy Delivery — Matched by Phone',
    sub: 'External API Event · Partner: Swiggy',
    accentColor: 'partner',
    tab: 'partner',
    activityType: 'food_court',
    eventsData: [
      {
        step: 'Customer places order on Swiggy',
        detail: 'App: <span>Swiggy</span> · Phone: <span>+91-XXXXXXXXXX</span> · <span>7:30 PM</span>',
      },
      {
        step: 'Partner webhook fires to CRM integration layer',
        detail: 'Event type: <span>order.placed</span> · Partner: <span>Swiggy</span> · Ref: <span>SWG-2025062291A</span>',
      },
      {
        step: 'CRM matches customer by mobile number',
        detail: 'Identifier: <span>phone</span> · Existing profile located · Customer ID resolved',
      },
      {
        step: 'External order event captured & linked',
        detail: 'Amount: <span>₹649</span> · Items: <span>2</span> · Channel: <span>Swiggy</span> · Status: <span>CONFIRMED</span>',
        tag: '<span class="flow-tag" style="background:rgba(234,88,12,0.15);color:#fb923c">External Partner</span>',
        payload: {
          amount: 649,
          quantity: 2,
          location_name: 'Swiggy – Partner API',
          payment_type: 'Swiggy Pay',
        },
        respTime: 156,
      },
    ],
  },
  {
    id: 'partner2',
    icon: '🍕',
    iconClass: 'partner-bg',
    name: 'Zomato Order — Matched by Email',
    sub: 'External API Event · Partner: Zomato',
    accentColor: 'partner',
    tab: 'partner',
    activityType: 'food_court',
    eventsData: [
      {
        step: 'Customer places order on Zomato',
        detail: 'App: <span>Zomato</span> · Email: <span>customer@example.com</span> · <span>1:15 PM</span>',
      },
      {
        step: 'Zomato sends order event via API integration',
        detail: 'Endpoint: <span>POST /partner/event</span> · Order ID: <span>ZMT-9900412</span> · Auth: <span>Bearer token</span>',
      },
      {
        step: 'CRM identifies customer by email',
        detail: 'Identifier: <span>email</span> · Profile found · Interaction logged to timeline',
      },
      {
        step: 'External interaction merged into customer 360 profile',
        detail: 'Amount: <span>₹892</span> · Items: <span>3</span> · Channel: <span>Zomato</span> · Profile: <span>ENRICHED</span>',
        tag: '<span class="flow-tag" style="background:rgba(234,88,12,0.15);color:#fb923c">External Partner</span>',
        payload: {
          amount: 892,
          quantity: 3,
          location_name: 'Zomato – Partner API',
          payment_type: 'Zomato Credits',
        },
        respTime: 178,
      },
    ],
  },
];

export const SCENARIOS_BY_TAB: Record<string, SimScenario[]> = {
  ev: EV_SCENARIOS,
  food: FOOD_SCENARIOS,
  mall: MALL_SCENARIOS,
  gas: GAS_SCENARIOS,
  charges: CHARGES_SCENARIOS,
  partner: PARTNER_SCENARIOS,
};

export const SIM_TABS = [
  { id: 'ev', label: 'EV Charging', color: 'var(--ev)' },
  { id: 'food', label: 'Food Court', color: 'var(--food)' },
  { id: 'mall', label: 'Mall Purchase', color: 'var(--mall)' },
  { id: 'gas', label: 'Gas Refill', color: 'var(--gas)' },
  { id: 'charges', label: 'Mall Charges', color: 'var(--gas2)' },
  { id: 'partner', label: 'External Partner', color: 'var(--partner)' },
] as const;
